import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import organizationService, {
  ClientCompany,
  Organization,
} from "../services/organizationService";
import { setActiveContextForApi } from "../shared/activeContextHolder";
import { STORAGE_KEYS } from "../shared/constants";
import { useAuth } from "./AuthContext";
import { usePlatform } from "@virtus-automation/platform-sdk";

export type ActiveContextType = "ORGANIZATION" | "CLIENT_COMPANY";

export interface ActiveContextState {
  type: ActiveContextType;
  organization: Organization | null;
  clientCompany: ClientCompany | null;
}

export interface ContextOptionsState {
  organization: Organization | null;
  client_companies: ClientCompany[];
  available_organizations?: Organization[];
  is_system_admin?: boolean;
}

interface ActiveContextValue {
  activeContext: ActiveContextState;
  contextOptions: ContextOptionsState | null;
  setActiveContext: (
    type: ActiveContextType,
    clientCompany?: ClientCompany | null,
  ) => void;
  switchOrganization: (organizationId: number) => Promise<void>;
  loadContextOptions: (
    silent?: boolean,
    organizationId?: number,
  ) => Promise<void>;
  isLoading: boolean;
  isRetrying?: boolean;
  error?: Error | null;
}

const defaultState: ActiveContextState = {
  type: "ORGANIZATION",
  organization: null,
  clientCompany: null,
};

const ActiveContext = createContext<ActiveContextValue | undefined>(undefined);

const storageKey = STORAGE_KEYS.ACTIVE_CONTEXT;

const adminOrgStorageKey = "virtus_admin_org_id";

function loadPersistedContext(): {
  type: ActiveContextType;
  clientCompanyId?: number;
} | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      type?: string;
      clientCompanyId?: number;
    };
    if (parsed.type === "CLIENT_COMPANY" && parsed.clientCompanyId != null) {
      return {
        type: "CLIENT_COMPANY",
        clientCompanyId: Number(parsed.clientCompanyId),
      };
    }
    if (parsed.type === "ORGANIZATION") return { type: "ORGANIZATION" };
    return null;
  } catch {
    return null;
  }
}

function persistContext(
  type: ActiveContextType,
  clientCompanyId?: number | string,
) {
  try {
    if (type === "CLIENT_COMPANY" && clientCompanyId != null) {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          type: "CLIENT_COMPANY",
          clientCompanyId: Number(clientCompanyId),
        }),
      );
    } else {
      localStorage.setItem(
        storageKey,
        JSON.stringify({ type: "ORGANIZATION" }),
      );
    }
  } catch (e) {
    console.warn("[ActiveContext] persist failed", e);
  }
}

function persistAdminOrgId(orgId: number | null) {
  try {
    if (orgId != null) {
      localStorage.setItem(adminOrgStorageKey, String(orgId));
    } else {
      localStorage.removeItem(adminOrgStorageKey);
    }
  } catch (e) {
    console.warn("[ActiveContext] persist adminOrgId failed", e);
  }
}

function loadPersistedAdminOrgId(): number | null {
  try {
    const val = localStorage.getItem(adminOrgStorageKey);
    return val ? parseInt(val, 10) : null;
  } catch {
    return null;
  }
}

export const ActiveContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, user, updateUser } = useAuth();
  const sdkSetContextOptions = usePlatform((state) => state.setContextOptions);
  const sdkSetActiveContext = usePlatform((state) => state.setActiveContext);
  const sdkSetListeners = usePlatform((state) => state.setListeners);

  const [activeContext, setActiveContextState] =
    useState<ActiveContextState>(defaultState);
  // Espelha activeContext para uso em callbacks sem precisar incluí-lo nas
  // deps (evitaria recriar loadContextOptions/etc a cada troca de contexto).
  const activeContextRef = useRef(activeContext);
  activeContextRef.current = activeContext;
  const [contextOptions, setContextOptionsState] =
    useState<ContextOptionsState | null>(null);
  const [adminOrgId, setAdminOrgIdState] = useState<number | null>(
    loadPersistedAdminOrgId(),
  );
  // Espelhos atualizados na hora (sem esperar o re-render): o seletor de empresas troca de organização
  // e logo em seguida seleciona um cliente dela, e o callback do SDK ainda pode ser o da renderização
  // anterior. Lendo daqui, o cliente é gravado junto com a organização nova, não com a antiga.
  const contextOptionsRef = useRef<ContextOptionsState | null>(null);
  const adminOrgIdRef = useRef<number | null>(adminOrgId);
  const setContextOptions = useCallback(
    (options: ContextOptionsState | null) => {
      contextOptionsRef.current = options;
      setContextOptionsState(options);
    },
    [],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isRetrying, setIsRetrying] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // Sync helper to update API holder synchronously
  const syncToApiSync = useCallback(
    (
      type: ActiveContextType,
      org: Organization | null,
      cc: ClientCompany | null,
      currentAdminOrgId: number | null,
    ) => {
      const isImpersonating =
        user?.is_admin && currentAdminOrgId && org?.id === currentAdminOrgId;

      if (type === "CLIENT_COMPANY" && cc) {
        setActiveContextForApi({
          type: "CLIENT_COMPANY",
          organizationId: org?.id ?? null,
          clientCompanyId: cc.id,
          adminOrganizationId: isImpersonating ? currentAdminOrgId : null,
        });
      } else if (org) {
        setActiveContextForApi({
          type: "ORGANIZATION",
          organizationId: org.id,
          clientCompanyId: null,
          adminOrganizationId: isImpersonating ? currentAdminOrgId : null,
        });
      } else {
        setActiveContextForApi(null);
      }
    },
    [user?.is_admin],
  );

  const setAdminOrgId = useCallback((id: number | null) => {
    adminOrgIdRef.current = id;
    setAdminOrgIdState(id);
    persistAdminOrgId(id);
  }, []);

  const loadContextOptions = useCallback(
    async (silent = false, organizationId?: number) => {
      if (!silent) setIsLoading(true);

      // Prioritize organizationId passed, then persisted adminOrgId
      const effectiveOrgId = organizationId || adminOrgId;

      try {
        const res = await organizationService.getContextOptions(
          effectiveOrgId || undefined,
        );
        if (!res.success) {
          setContextOptions({
            organization: null,
            client_companies: [],
            available_organizations: [],
            is_system_admin: false,
          });
          // Só reseta para o estado vazio se ainda não tínhamos um contexto
          // resolvido — não descarta um contexto já ativo só porque essa
          // revalidação em segundo plano falhou (evita re-disparar todos os
          // carregamentos da página com org_id vazio).
          setActiveContextState((prev) =>
            prev.organization ? prev : defaultState,
          );
          if (!activeContextRef.current.organization)
            setActiveContextForApi(null);
          return;
        }
        const options: ContextOptionsState = {
          organization: res.organization ?? null,
          client_companies: res.client_companies ?? [],
          available_organizations: res.available_organizations ?? [],
          is_system_admin: res.is_system_admin ?? false,
        };

        const persisted = loadPersistedContext();
        const org = options.organization;
        const clients = options.client_companies;
        const allowedClientIds = new Set(clients.map((c) => c.id));

        let finalState: ActiveContextState;

        if (
          persisted?.type === "CLIENT_COMPANY" &&
          persisted.clientCompanyId != null
        ) {
          if (allowedClientIds.has(persisted.clientCompanyId)) {
            const cc =
              clients.find((c) => c.id === persisted.clientCompanyId) ?? null;
            finalState = {
              type: "CLIENT_COMPANY",
              organization: org,
              clientCompany: cc,
            };
          } else {
            persistContext("ORGANIZATION");
            finalState = {
              type: "ORGANIZATION",
              organization: org,
              clientCompany: null,
            };
          }
        } else {
          finalState = {
            type: "ORGANIZATION",
            organization: org,
            clientCompany: null,
          };
        }

        // Sync synchronously locally
        setActiveContextState(finalState);
        setContextOptions(options);

        // Sincronizar contexto ativo com SDK PRIMEIRO
        if (finalState.type === "CLIENT_COMPANY" && finalState.clientCompany) {
          sdkSetActiveContext(
            "CLIENT_COMPANY",
            finalState.clientCompany as any,
          );
        } else {
          sdkSetActiveContext("ORGANIZATION");
        }

        // Garantir que a organização ativa atual seja a PRIMEIRA na lista.
        // O SDK usa a primeira da lista como fallback quando o contexto não tem organização definida.
        const sortedAvailableOrgs = [
          ...(options.available_organizations || []),
        ];
        if (options.organization) {
          const orgId = options.organization.id;
          const idx = sortedAvailableOrgs.findIndex((o) => o.id === orgId);
          if (idx > 0) {
            const [activeOrg] = sortedAvailableOrgs.splice(idx, 1);
            sortedAvailableOrgs.unshift(activeOrg);
          } else if (idx === -1) {
            sortedAvailableOrgs.unshift(options.organization);
          }
        }

        // Sincronizar opções com SDK DEPOIS
        sdkSetContextOptions({
          is_system_admin: options.is_system_admin || false,
          available_organizations: sortedAvailableOrgs as any,
          client_companies: (options.client_companies as any) || [],
        });

        setError(null);
        setRetryCount(0);
        setIsRetrying(false);

        if (user && res.organization) {
          const orgRole = ((res as { current_user_org_role?: string })
            .current_user_org_role ??
            user.org_role ??
            "EDITOR") as any;

          // Só atualiza se houver mudança real para evitar loops
          if (
            user.organization_id !== Number(res.organization.id) ||
            user.org_role !== orgRole
          ) {
            updateUser({
              ...user,
              organization_id: Number(res.organization.id),
              org_role: orgRole,
            });
          }
        }

        syncToApiSync(
          finalState.type,
          finalState.organization,
          finalState.clientCompany,
          effectiveOrgId || null,
        );
      } catch (error) {
        setContextOptions({
          organization: null,
          client_companies: [],
          available_organizations: [],
          is_system_admin: false,
        });
        // Idem: não descarta um contexto já resolvido só porque essa
        // tentativa de revalidação falhou (ex.: backend temporariamente
        // fora do ar) — mantém a página funcionando com o último contexto
        // conhecido em vez de resetar tudo e disparar um novo round de
        // carregamentos sem org_id.
        const hadOrganization = !!activeContextRef.current.organization;
        setActiveContextState((prev) =>
          prev.organization ? prev : defaultState,
        );
        if (!hadOrganization) setActiveContextForApi(null);
        setError(
          error instanceof Error
            ? error
            : new Error("Falha ao carregar contexto"),
        );

        // Agendar retry se autenticado e não silencioso
        if (!silent && isAuthenticated) {
          setIsRetrying(true);
          const nextRetry = Math.min(
            Math.pow(2, retryCount) * 1000 + 4000,
            30000,
          ); // 5s, 8s, 12s... max 30s
          console.log(
            `[ActiveContext] Agendando tentativa de reconexão em ${nextRetry / 1000}s...`,
          );
          setTimeout(() => {
            setRetryCount((prev) => prev + 1);
            loadContextOptions(true); // Tenta de novo silenciosamente
          }, nextRetry);
        }
      } finally {
        setIsLoading(false);
        if (organizationId != null && user?.is_admin) {
          setAdminOrgId(organizationId);
        }
      }
    },
    [
      user,
      updateUser,
      setContextOptions,
      adminOrgId,
      setAdminOrgId,
      syncToApiSync,
      isAuthenticated,
      retryCount,
      sdkSetContextOptions,
      sdkSetActiveContext,
    ],
  );

  const setActiveContext = useCallback(
    (type: ActiveContextType, clientCompany?: ClientCompany | null) => {
      const org = contextOptionsRef.current?.organization ?? null;
      const currentAdminOrgId = adminOrgIdRef.current;
      if (type === "CLIENT_COMPANY" && clientCompany) {
        persistContext("CLIENT_COMPANY", clientCompany.id);
        setActiveContextState({
          type: "CLIENT_COMPANY",
          organization: org,
          clientCompany,
        });
        syncToApiSync("CLIENT_COMPANY", org, clientCompany, currentAdminOrgId);
      } else {
        persistContext("ORGANIZATION");
        setActiveContextState({
          type: "ORGANIZATION",
          organization: org,
          clientCompany: null,
        });
        syncToApiSync("ORGANIZATION", org, null, currentAdminOrgId);
      }
    },
    [syncToApiSync],
  );

  const switchOrganization = useCallback(
    async (organizationId: number) => {
      await loadContextOptions(false, organizationId);
    },
    [loadContextOptions],
  );

  // Keep sync effect as a backup for when user object or adminOrgId changes independently
  useEffect(() => {
    syncToApiSync(
      activeContext.type,
      activeContext.organization,
      activeContext.clientCompany,
      adminOrgId,
    );
  }, [
    activeContext.type,
    activeContext.organization?.id,
    activeContext.clientCompany?.id,
    adminOrgId,
    user?.is_admin,
    syncToApiSync,
  ]);

  useEffect(() => {
    if (isAuthenticated) loadContextOptions();
    else {
      setContextOptions(null);
      setActiveContextState(defaultState);
      setIsLoading(false);
    }
  }, [isAuthenticated, loadContextOptions, setContextOptions]);

  // Sincronizar Listeners do SDK para o Frontend
  useEffect(() => {
    sdkSetListeners({
      onContextChange: (newContext) => {
        // Quando o usuário interagir com o TenantSelector na UI, atualizamos o state local
        setActiveContext(newContext.type, newContext.clientCompany as any);
      },
      onAdminSwitchOrg: async (orgId) => {
        // Quando o admin buscar e trocar de org mestre
        await switchOrganization(Number(orgId));
      },
    });
  }, [sdkSetListeners, setActiveContext, switchOrganization]);

  const value: ActiveContextValue = useMemo(
    () => ({
      activeContext,
      contextOptions,
      setActiveContext,
      switchOrganization,
      loadContextOptions,
      isLoading,
      isRetrying,
      error,
    }),
    [
      activeContext,
      contextOptions,
      setActiveContext,
      switchOrganization,
      loadContextOptions,
      isLoading,
      isRetrying,
      error,
    ],
  );

  return (
    <ActiveContext.Provider value={value}>{children}</ActiveContext.Provider>
  );
};

export function useActiveContext(): ActiveContextValue {
  const ctx = useContext(ActiveContext);
  if (ctx === undefined)
    throw new Error(
      "useActiveContext must be used within ActiveContextProvider",
    );
  return ctx;
}
